import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
  Table,
  Timeline,
  Space,
  Tag,
} from "antd";
import ErrorBoundary from "../common/ErrorBoundary";
import {
  DollarSign,
  Building2,
  Trophy,
  CheckCircle2,
  Clock,
  Users,
  Key,
  BarChart3,
  FileText,
  ShieldCheck,
  LayoutDashboard,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import { formatTradeType, getTradeTypeColor, formatDateTime, formatVolume } from "../../utils/formatters";
import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [recentActivity, setRecentActivity] = useState({});
  const [systemHealth, setSystemHealth] = useState({});

  // 快速导航配置
  const quickActions = [
    {
      title: "用户管理",
      description: "管理系统用户和权限",
      icon: <Users size={24} />,
      path: "/users",
      color: "var(--color-primary)",
      bgColor: "var(--color-primary-light)",
    },
    {
      title: "Token管理",
      description: "管理API访问令牌",
      icon: <Key size={24} />,
      path: "/tokens",
      color: "var(--color-success)",
      bgColor: "var(--color-success-light)",
    },
    {
      title: "交易数据",
      description: "查看交易量数据",
      icon: <BarChart3 size={24} />,
      path: "/trade-data",
      color: "var(--color-secondary)",
      bgColor: "var(--color-secondary-light)",
    },
    {
      title: "报告生成",
      description: "生成和下载报告",
      icon: <FileText size={24} />,
      path: "/reports",
      color: "var(--color-warning)",
      bgColor: "var(--color-warning-light)",
    },
  ];

  // 获取仪表盘数据
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // 并行获取所有数据
      const [statsRes, activityRes] = await Promise.all([
        apiService.dashboard.getStats(),
        apiService.dashboard.getRecentActivity(),
      ]);

      // Handle standard response format: {success: true, data: {...}, message: "..."}
      if (!statsRes.data.success) {
        throw new Error(statsRes.data.message || "获取统计数据失败");
      }
      if (!activityRes.data.success) {
        throw new Error(activityRes.data.message || "获取活动数据失败");
      }

      setStats(statsRes.data.data || {});
      setRecentActivity(activityRes.data.data || {});
      setSystemHealth(activityRes.data.data?.system_status || {});
    } catch (error) {
      console.error("获取仪表盘数据失败:", error);
      setError(
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "获取数据失败，请稍后重试",
      );
    } finally {
      setLoading(false);
    }
  };

  // 格式化数字
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num?.toString() || "0";
  };

  // 格式化交易量
  const formatVolume = (volume) => {
    if (volume >= 1000000) {
      return (volume / 1000000).toFixed(2) + "M";
    } else if (volume >= 1000) {
      return (volume / 1000).toFixed(2) + "K";
    }
    return volume?.toFixed(2) || "0.00";
  };

  // 格式化时间
  const formatTime = (timeStr) => {
    return formatDateTime(timeStr);
  };

  // 最近交易表格列
  const tradeColumns = [
    {
      title: "交易所",
      dataIndex: "exchange",
      key: "exchange",
      width: 100,
    },
    {
      title: "类型",
      dataIndex: "trade_type",
      key: "trade_type",
      width: 80,
      render: (tradeType) => (
        <Tag color={getTradeTypeColor(tradeType)}>
          {formatTradeType(tradeType)}
        </Tag>
      ),
    },
    {
      title: "交易对",
      dataIndex: "pair",
      key: "pair",
      width: 100,
    },
    {
      title: "交易量",
      dataIndex: "volume",
      key: "volume",
      width: 120,
      render: (volume) => formatVolume(volume),
    },
    {
      title: "时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (time) => formatTime(time),
    },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
        <div className="dashboard-loading-text">加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        className="dashboard-error"
      />
    );
  }

  return (
    <ErrorBoundary>
      <div className="dashboard">
        {/* 页面标题 */}
        <div className="dashboard-header animate-fade-in">
          <div className="dashboard-title-row">
            <LayoutDashboard className="dashboard-title-icon" size={32} />
            <div className="dashboard-title-content">
              <h1 className="dashboard-title">交易量管理系统仪表盘</h1>
              <p className="dashboard-subtitle">
                实时监控系统状态和交易数据概览
              </p>
            </div>
          </div>
        </div>

        {/* 统计卡片 - Bento Grid 布局 */}
        <Row gutter={[16, 16]} className="dashboard-stats-row">
          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              className="stat-card stat-card-primary animate-fade-in"
              style={{ animationDelay: "0.1s" }}
            >
              <div className="stat-card-icon stat-icon-primary">
                <DollarSign size={24} />
              </div>
              <div className="stat-card-content">
                <div className="stat-card-label">总交易量</div>
                <div className="stat-card-value">
                  {formatVolume(stats.total_trade_volume || 0)}
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              className="stat-card stat-card-secondary animate-fade-in"
              style={{ animationDelay: "0.2s" }}
            >
              <div className="stat-card-icon stat-icon-secondary">
                <Building2 size={24} />
              </div>
              <div className="stat-card-content">
                <div className="stat-card-label">活跃交易所</div>
                <div className="stat-card-value">
                  {stats.total_exchanges || 0}
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              className="stat-card stat-card-accent animate-fade-in"
              style={{ animationDelay: "0.3s" }}
            >
              <div className="stat-card-icon stat-icon-accent">
                <Trophy size={24} />
              </div>
              <div className="stat-card-content">
                <div className="stat-card-label">昨日交易量</div>
                <div className="stat-card-value">
                  {formatVolume(stats.yesterday_volume || 0)}
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={6}>
            <Card
              className={`stat-card stat-card-status ${systemHealth.status === "healthy" ? "status-healthy" : "status-error"} animate-fade-in`}
              style={{ animationDelay: "0.4s" }}
            >
              <div
                className={`stat-card-icon ${systemHealth.status === "healthy" ? "status-icon-healthy" : "status-icon-error"}`}
              >
                {systemHealth.status === "healthy" ? (
                  <CheckCircle2 size={24} />
                ) : (
                  <Clock size={24} />
                )}
              </div>
              <div className="stat-card-content">
                <div className="stat-card-label">系统状态</div>
                <div className="stat-card-value">
                  {systemHealth.status === "healthy" ? "健康" : "异常"}
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 详细统计 - 玻璃态卡片 */}
        <Row gutter={[16, 16]} className="dashboard-detail-row">
          <Col xs={24} sm={12} md={12} lg={8}>
            <Card
              className="detail-card glass animate-fade-in"
              style={{ animationDelay: "0.5s" }}
            >
              <div className="detail-card-header">
                <Users className="detail-card-icon" size={20} />
                <span className="detail-card-title">用户统计</span>
              </div>
              <div className="detail-card-body">
                <div className="detail-stat">
                  <div className="detail-stat-label">总用户数</div>
                  <div className="detail-stat-value">
                    {stats.total_users || 0}
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-label">活跃用户</div>
                  <div className="detail-stat-value detail-stat-value-primary">
                    {stats.active_users || 0}
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={8}>
            <Card
              className="detail-card glass animate-fade-in"
              style={{ animationDelay: "0.6s" }}
            >
              <div className="detail-card-header">
                <ShieldCheck className="detail-card-icon" size={20} />
                <span className="detail-card-title">Token统计</span>
              </div>
              <div className="detail-card-body">
                <div className="detail-stat">
                  <div className="detail-stat-label">总Token数</div>
                  <div className="detail-stat-value">
                    {stats.total_tokens || 0}
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-label">活跃Token</div>
                  <div className="detail-stat-value detail-stat-value-success">
                    {stats.active_tokens || 0}
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={12} lg={8}>
            <Card
              className="detail-card glass animate-fade-in"
              style={{ animationDelay: "0.7s" }}
            >
              <div className="detail-card-header">
                <BarChart3 className="detail-card-icon" size={20} />
                <span className="detail-card-title">交易统计</span>
              </div>
              <div className="detail-card-body">
                <div className="detail-stat">
                  <div className="detail-stat-label">交易对数</div>
                  <div className="detail-stat-value">
                    {stats.total_pairs || 0}
                  </div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-label">活跃度</div>
                  <div className="detail-stat-value detail-stat-value-accent">
                    {stats.total_users
                      ? Math.round(
                        (stats.active_users / stats.total_users) * 100,
                      )
                      : 0}
                    %
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 快速导航 - Bento Grid 风格 */}
        <Row gutter={[16, 16]} className="dashboard-actions-row">
          <Col span={24}>
            <Card
              className="glass animate-fade-in"
              style={{ animationDelay: "0.8s" }}
            >
              <div className="actions-header">
                <LayoutDashboard size={20} />
                <span className="actions-title">快速导航</span>
              </div>
              <Row gutter={[16, 16]}>
                {quickActions.map((action, index) => (
                  <Col xs={24} sm={12} lg={6} key={index}>
                    <div
                      className="quick-action-card"
                      onClick={() => navigate(action.path)}
                      style={{
                        "--action-color": action.color,
                        "--action-bg": action.bgColor,
                      }}
                    >
                      <div className="quick-action-icon-wrapper">
                        <div className="quick-action-icon">{action.icon}</div>
                      </div>
                      <div className="quick-action-content">
                        <div className="quick-action-title">{action.title}</div>
                        <div className="quick-action-description">
                          {action.description}
                        </div>
                      </div>
                      <ChevronRight className="quick-action-arrow" size={20} />
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        {/* 最近活动与系统信息 */}
        <Row gutter={[16, 16]} className="dashboard-activity-row">
          <Col xs={24} lg={12}>
            <Card
              className="glass animate-fade-in"
              title={
                <div className="card-title-with-icon">
                  <BarChart3 size={18} />
                  <span>最近交易</span>
                </div>
              }
              extra={
                <span className="card-extra-text">
                  最后更新: {formatTime(systemHealth.last_trade_time)}
                </span>
              }
              style={{ animationDelay: "0.9s" }}
            >
              <Table
                dataSource={recentActivity.recent_trades || []}
                columns={tradeColumns}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content', y: 300 }}
                rowKey="id"
              />
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              className="glass animate-fade-in"
              title={
                <div className="card-title-with-icon">
                  <LayoutDashboard size={18} />
                  <span>系统信息</span>
                </div>
              }
              extra={<span className="card-extra-text">实时监控</span>}
              style={{ animationDelay: "1.0s" }}
            >
              <Timeline
                size="small"
                className="system-timeline"
                items={[
                  {
                    color: "green",
                    dot: (
                      <CheckCircle2
                        size={14}
                        className="timeline-dot timeline-dot-success"
                      />
                    ),
                    children: (
                      <div className="timeline-content">
                        <div className="timeline-label">数据库状态</div>
                        <div className="timeline-value">
                          {systemHealth.status === "healthy" ? "正常" : "异常"}
                        </div>
                        <div className="timeline-desc">连接状态良好</div>
                      </div>
                    ),
                  },
                  {
                    color: "blue",
                    dot: (
                      <Clock
                        size={14}
                        className="timeline-dot timeline-dot-info"
                      />
                    ),
                    children: (
                      <div className="timeline-content">
                        <div className="timeline-label">最近登录</div>
                        <div className="timeline-value">
                          {formatTime(systemHealth.last_login_time)}
                        </div>
                        <div className="timeline-desc">用户活跃度正常</div>
                      </div>
                    ),
                  },
                  {
                    color: "purple",
                    dot: (
                      <BarChart3
                        size={14}
                        className="timeline-dot timeline-dot-accent"
                      />
                    ),
                    children: (
                      <div className="timeline-content">
                        <div className="timeline-label">数据更新</div>
                        <div className="timeline-value">
                          {formatTime(systemHealth.last_trade_time)}
                        </div>
                        <div className="timeline-desc">交易数据实时同步</div>
                      </div>
                    ),
                  },
                  {
                    dot: (
                      <Clock
                        size={14}
                        className="timeline-dot timeline-dot-tertiary"
                      />
                    ),
                    children: (
                      <div className="timeline-content">
                        <div className="timeline-label">系统运行时间</div>
                        <div className="timeline-value">
                          {systemHealth.uptime || "未知"}
                        </div>
                        <div className="timeline-desc">系统稳定运行中</div>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </ErrorBoundary>
  );
};

export default Dashboard;
